import { apiClient, getData, getPage, type Page } from '@/api/client'
import type { components, operations } from '@/api/schema'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/**
 * The query string the archive accepts, taken from the generated schema rather
 * than restated — same rule as `marketApi.ts`. A hand-written interface is how
 * a parameter mm-be added goes unnoticed for a day: the generated types have
 * it, the interface does not, and nothing fails to compile.
 */
type ArchiveQuery = NonNullable<operations['api_v5_newsletters_list']['parameters']['query']>

/**
 * The newsletter seam: three read routes in, wire types out.
 *
 * All three are `V5PublicAPIView` (`security: [{}]` in the spec) — no bearer
 * token required and none is attached by these calls. `api/client.ts`'s auth
 * middleware still adds one when a session happens to exist, which is
 * harmless: the view ignores it. What matters is that nothing here is gated on
 * a session, so a signed-out reader gets the archive.
 */
export const newsletterApi = {
  /** One page of the published archive, newest first. */
  archive: (query: ArchiveQuery): Promise<Page<Wire<'NewsletterCard'>>> =>
    getPage(() => apiClient.GET('/api/v5/newsletters/', { params: { query } })),

  /**
   * The most recent published issue — the archive hero.
   *
   * 404 `NEWSLETTER_NOT_FOUND` when NOTHING is published yet, which is the same
   * code an unknown issue number raises. `newsletterFailure` in `errors.ts`
   * cannot tell those apart from the response alone, and neither can the
   * backend's own caller; the call site knows which route it asked for.
   */
  latest: (): Promise<Wire<'NewsletterDetail'>> =>
    getData(() => apiClient.GET('/api/v5/newsletters/latest/')),

  /**
   * One published issue by number.
   *
   * The route key is `issue_number` (an integer), NOT `slug`. `slug` rides on
   * the payload and is decorative — there is no route that accepts it, so a
   * screen that builds its URL from `card.slug` 404s on every issue.
   */
  issue: (issueNumber: number): Promise<Wire<'NewsletterDetail'>> =>
    getData(() =>
      apiClient.GET('/api/v5/newsletters/{issue_number}/', {
        params: { path: { issue_number: issueNumber } },
      }),
    ),
}
